//
// Copyright 2025 DXOS.org
//

import * as Context from 'effect/Context';
import * as Effect from 'effect/Effect';
import * as Layer from 'effect/Layer';
import * as Schema from 'effect/Schema';
import * as Stream from 'effect/Stream';

import type { CleanupFn } from '@dxos/async';
import { invariant } from '@dxos/invariant';
import { type SpaceId, type URI } from '@dxos/keys';

import type * as Blob from './Blob';
import type * as Entity from './Entity';
import * as Err from './Err';
import type * as Feed from './Feed';
import type * as Filter from './Filter';
import type * as Hypergraph from './Hypergraph';
import { type AnyProperties, EntityKind, KindId } from './internal/common/types';
// Deep import (not the `./internal/Entity` barrel) to avoid a cycle:
// Database → internal/Entity → entity → JsonSchema → Ref → Database.
import { isInstanceOf } from './internal/Entity/type-uri';
import * as queryInternal from './internal/Query';
import type { Ref } from './internal/Ref/ref';
import type * as Obj from './Obj';
import type * as Query from './Query';
import type * as QueryResult from './QueryResult';
import type * as Registry from './Registry';
import type * as Type from './Type';

/**
 * `query` API function declaration.
 */
// TODO(burdon): Reconcile Query and Filter (should only have one root type).
export interface QueryFn {
  <Q extends Query.Any>(query: Q): QueryResult.QueryResult<Query.Type<Q>>;
  <F extends Filter.Any>(filter: F): QueryResult.QueryResult<Filter.Type<F>>;
}

/**
 * Common interface for Database, Feed, and Hypergraph.
 */
export interface Queryable {
  query: QueryFn;
}

export type GetObjectByIdOptions = {
  deleted?: boolean;
};

export type ObjectPlacement = 'root-doc' | 'linked-doc';

export type AddOptions = {
  /**
   * Where to place the object in the Automerge document tree.
   * Root document is always loaded with the space.
   * Linked documents are loaded lazily.
   * Placing large number of objects in the root document may slow down the initial load.
   *
   * @default 'linked-doc'
   */
  placeIn?: ObjectPlacement;

  /**
   * Append the object to this feed instead of the automerge-backed space database. The object is
   * returned synchronously (a live feed object) and persisted in the background — confirm the write
   * completed with {@link Database.flush}. Synchronous alternative to the async
   * {@link Database.appendToFeed}; `placeIn` is ignored when set.
   */
  to?: Feed.Feed;
};

/**
 * Rejects Type entities from {@link Database.add} at compile time via their `[KindId]` brand. Used
 * as `T & RejectTypeEntity<T>` to preserve inference of `T`. Bounding `add` on
 * `Obj.Unknown | Relation.Unknown` instead would reject broadly-typed instance adds (e.g.
 * `Entity.Any`, `Obj.OfShape<T>`), forcing casts repo-wide.
 */
export type RejectTypeEntity<T> = T extends { readonly [KindId]: EntityKind.Type }
  ? { __error: 'Type entities must be persisted via db.addType(), not db.add().' }
  : T;

export type FlushOptions = {
  /**
   * Write any pending changes to disk.
   * @default true
   */
  disk?: boolean;

  /**
   * Wait for pending index updates.
   * @default true
   */
  indexes?: boolean;

  /**
   * Flush pending updates to objects and queries.
   * @default false
   */
  updates?: boolean;
};

/**
 * A caller-owned, writable **independent instance** of one object bound to one branch: a distinct
 * object instance (not a UI surface), separate from the device-global canonical object.
 * @see Database.branch
 */
export type BranchBinding<T extends Obj.Unknown = Obj.Unknown> = {
  /** Live object bound to the branch document (`'main'` -> the canonical live object). */
  readonly object: T;
  /** Release the binding (drops the doc-handle listener; never deletes the branch document). */
  dispose(): void;
};

/**
 * Identifier denoting an ECHO Database.
 */
export const TypeId = Symbol.for('@dxos/echo/Database');
export type TypeId = typeof TypeId;

/**
 * ECHO Database interface.
 */
export interface Database extends Queryable {
  readonly [TypeId]: TypeId;

  get spaceId(): SpaceId;

  get graph(): Hypergraph.Hypergraph;

  /**
   * Registry for this database. Delegates type lookups to the shared hypergraph registry.
   * To persist a schema so it replicates to other clients, add the type entity with
   * {@link addType} (e.g. `await db.addType(Type.makeObjectFromJsonSchema(...))`).
   */
  readonly registry: Registry.Registry;

  toJSON(): object;

  /**
   * Return object by local ID.
   * @deprecated Use `db.query(Filter.id(id)).runSync()[0]` for a working-set lookup, or resolve via a {@link Ref}.
   */
  getObjectById<T extends Obj.Unknown = Obj.OfShape<AnyProperties>>(
    id: string,
    opts?: GetObjectByIdOptions,
  ): T | undefined;

  /**
   * Query objects.
   */
  query: QueryFn;

  /**
   * Creates a reference to an existing object in the database.
   *
   * NOTE: The reference may be dangling if the object is not present in the database.
   * NOTE: Difference from `Ref.fromURI`
   * `Ref.fromURI(dxn)` returns an unhydrated reference. The `.load` and `.target` APIs will not work.
   * `db.makeRef(dxn)` is preferable in cases with access to the database.
   */
  makeRef<T extends Entity.Unknown = Entity.Unknown>(uri: URI.URI): Ref<T>;

  /**
   * Adds an object or relation to the database.
   *
   * Only Object and Relation entities are accepted. To persist a Type definition use
   * {@link addType} — passing a Type entity is rejected at compile time (and at runtime).
   *
   * Pass `{ to: feed }` to append to a feed instead (synchronous; confirm with {@link flush}).
   */
  add<T extends Entity.Unknown = Entity.Unknown>(obj: T & RejectTypeEntity<T>, opts?: AddOptions): T;

  /**
   * Persists a Type definition (clones/forks the entity) so it replicates to other peers.
   *
   * Runs a conflict query first: if a type with the same typename + version already exists in
   * this space, the existing persisted entity is returned and no duplicate is created. This is
   * the only supported way to add Type entities — {@link add} rejects them.
   */
  addType<T extends Type.AnyEntity>(type: T): Promise<T>;

  /**
   * Removes object from the database.
   */
  // TODO(burdon): Return true if removed (currently throws if not present).
  remove(obj: Entity.Unknown): void;

  /**
   * Appends entities to a feed.
   *
   * The feed must already be stored in the database (added via {@link add}); its underlying
   * queue is addressed by the feed object's URI.
   */
  appendToFeed(feed: Feed.Feed, entities: Entity.Unknown[]): Promise<void>;

  /**
   * Removes entities from a feed.
   */
  deleteFromFeed(feed: Feed.Feed, entities: Entity.Unknown[]): Promise<void>;

  /**
   * Wait for all pending changes to be saved to disk.
   * Optionaly waits for changes to be propagated to indexes and event handlers.
   */
  flush(opts?: FlushOptions): Promise<void>;

  //
  // Branching. A branch is a writable alternate timeline of an object subtree (same object ids,
  // shared automerge history, true CRDT merge-back). The registry is synced on the space root;
  // the currently-viewed branch stays device-local.
  //

  /**
   * The device-global current branch for an object id (`'main'` by default).
   * @deprecated Prefer `Obj.getBranch(obj)` — it takes the object and reports the branch of that
   * specific instance (including `db.branch()` independent instances), not just the device selection.
   */
  getCurrentBranch(objectId: string): string;

  /**
   * An immutable snapshot of the object at the given historical heads — a detached instance, not a
   * pin on the live object. Prefer `Obj.getVersion(obj, heads)`.
   */
  getVersion<T extends Obj.Unknown>(obj: T, heads: readonly string[]): Obj.Snapshot<T>;

  /** All branch names available for an object, including the implicit `'main'` (always first). */
  listBranches(objectId: string): string[];

  /**
   * Fork the object and its referenced subtree into a new branch (does not switch to it).
   * @param opts.fromHeads Fork from a historical frontier instead of the tip (a bare heads array
   *   applies to the root only; a map forks each member from its own frontier).
   */
  createBranch(
    rootObjectId: string,
    name: string,
    opts?: { fromHeads?: readonly string[] | Record<string, readonly string[]> },
  ): Promise<void>;

  /** Switch the object's subtree to a branch (or back to `'main'`). Device-local; cascades to children. */
  switchBranch(rootObjectId: string, name: string): Promise<void>;

  /** Merge a branch back into main across the subtree, then switch back to main. */
  mergeBranch(rootObjectId: string, name: string, opts?: { deleteAfter?: boolean }): Promise<void>;

  /** Fold main's changes into a branch across the subtree (the reverse of {@link mergeBranch}). */
  syncBranch(rootObjectId: string, name: string): Promise<void>;

  /** Delete a branch (its documents lose their sync reference). Cannot delete `'main'`. */
  deleteBranch(rootObjectId: string, name: string): void;

  /**
   * Create a caller-owned, writable binding to one branch of one object — a live object whose reads
   * resolve the branch document and whose writes land on the branch document only. Multiple bindings
   * to different branches of the same object may coexist; the device-global current branch and other
   * bindings are unaffected. Binding to `'main'` returns the canonical live object. Bindings are
   * ephemeral and never persisted — the caller must `dispose()`.
   */
  branch<T extends Obj.Unknown>(obj: T, name: string): Promise<BranchBinding<T>>;

  /**
   * Removes feed items by ID.
   */
  removeFeedItemsByIds(feed: Feed.Feed, ids: string[]): Promise<void>;

  /**
   * Syncs a feed with the server.
   */
  syncFeed(feed: Feed.Feed, options?: Feed.SyncOptions): Promise<void>;

  /**
   * Returns queue replication backlog for the feed's namespace.
   */
  getFeedSyncState(feed: Feed.Feed): Promise<Feed.SyncState>;

  /**
   * Disposes and drops the in-memory handle (live working-set / core cache) for a feed, so the next
   * access re-reads it cold. Advanced cache-control; primarily used by tests to model a spawned
   * process reading the feed with an empty in-memory cache. Public (not `_`-prefixed) so it survives
   * declaration stripping for cross-package test use.
   */
  evictFeedHandle(feed: Feed.Feed): Promise<void>;

  /**
   * Hashes and uploads `bytes` via the chosen storage backend, returning an un-added Blob object.
   * Rejects with `Err.BlobTooLargeError` (over inline storage's fixed cap, or the backend's own
   * `maxSize`), `Err.BlobWriteError` (backend upload failure), or `Err.BlobNotAvailableError`
   * (`reason: 'backend-not-registered'` — the requested storage name has no registered backend).
   */
  createBlob(bytes: Uint8Array, options?: { type?: string; storage?: string }): Promise<Blob.Blob>;

  /**
   * Loads a blob's bytes. Rejects with `Err.BlobNotAvailableError` if the backend for the blob's
   * storage scheme is not registered, offline, or cannot find the bytes.
   */
  readBlob(blob: Blob.Blob): Promise<Uint8Array>;

  /**
   * Checks whether a blob's bytes are currently available.
   */
  blobExists(blob: Blob.Blob): Promise<boolean>;

  /**
   * Returns a renderable URL for the blob, if one can be produced.
   */
  getBlobUrl(blob: Blob.Blob): Promise<string | undefined>;

  /**
   * Get the current combined (automerge documents + feed blocks) sync state, reported against a
   * single remote peer.
   */
  getSyncState(options?: GetSyncStateOptions): Promise<SyncState>;

  /**
   * Subscribe to combined sync state changes.
   */
  subscribeToSyncState(cb: (state: SyncState) => void, options?: GetSyncStateOptions): CleanupFn;
}

export const isDatabase = (obj: unknown): obj is Database => {
  return obj ? typeof obj === 'object' && TypeId in obj && obj[TypeId] === TypeId : false;
};

export const Database: Schema.Schema<Database> = Schema.Any.pipe(Schema.filter((space) => isDatabase(space)));

/**
 * Effect service tag for Database dependency injection.
 */
export class Service extends Context.Tag('@dxos/echo/Database/Service')<
  Service,
  {
    readonly db: Database;
  }
>() {}

/**
 * Layer that provides a Database service that throws when accessed.
 * Useful as a default layer when no database is available.
 */
export const notAvailable = Layer.succeed(Service, {
  get db(): Database {
    throw new Error('Database not available');
  },
});

/**
 * Creates a Database service instance from a Database.
 */
export const makeService = (db: Database): Context.Tag.Service<Service> => {
  return {
    get db() {
      return db;
    },
  };
};

/**
 * Creates a Layer that provides the Database service.
 */
export const layer = (db: Database): Layer.Layer<Service> => {
  return Layer.succeed(Service, makeService(db));
};

/**
 * Returns the space ID of the database.
 */
export const spaceId = Effect.gen(function* () {
  const { db } = yield* Service;
  return db.spaceId;
});

/**
 * Resolves an object by its DXN.
 */
export const resolve: {
  // No type check.
  (ref: URI.URI | Ref<any>): Effect.Effect<Entity.Unknown, never, Service>;
  // Check matches schema.
  <S extends Type.AnyEntity>(
    ref: URI.URI | Ref<any>,
    schema: S,
  ): Effect.Effect<Type.InstanceType<S>, Err.EntityNotFoundError, Service>;
} = (<S extends Type.AnyEntity>(
  ref: URI.URI | Ref<any>,
  schema?: S,
): Effect.Effect<Type.InstanceType<S>, Err.EntityNotFoundError, Service> =>
  Effect.gen(function* () {
    const { db } = yield* Service;
    const dxn = typeof ref === 'string' ? ref : ref.uri;
    const object = yield* Effect.promise(() =>
      db.graph
        .createRefResolver({
          context: {
            space: db.spaceId,
          },
        })
        .resolveLegacy(dxn),
    );

    if (!object) {
      return yield* Effect.fail(new Err.EntityNotFoundError(dxn));
    }
    // `isInstanceOf` uses a conditional generic that TS can't resolve through
    // the local `S extends Type.AnyEntity` parameter — runtime accepts it fine.
    invariant(!schema || isInstanceOf(schema as any, object), 'Object type mismatch.');
    return object as any;
  }).pipe(Effect.withSpan('Database.resolve'))) as any;

/**
 * Loads an object reference.
 *
 * Catching not found error:
 *
 * ```ts
 * yield* load(ref).pipe(Effect.catchTag('EntityNotFoundError', () => Effect.succeed(undefined)));
 * ```
 *
 */
export const load: <T>(ref: Ref<T>) => Effect.Effect<T, Err.EntityNotFoundError, never> = Effect.fn('Database.load')(
  function* (ref) {
    const object = yield* Effect.promise(() => ref.tryLoad());
    if (!object) {
      return yield* Effect.fail(new Err.EntityNotFoundError(ref.uri));
    }
    return object;
  },
);

/**
 * Adds an object or relation to the database.
 * @see {@link Database.add}
 */
// The Effect wrapper intentionally omits the method's `opts` (e.g. `{ to: feed }`): it is applied
// point-free (`Effect.forEach(Database.add)`), where a second parameter would collide with the
// iteratee index. Effect-style feed appends go through `Database.appendToFeed` / `Feed.append`.
export const add = <T extends Entity.Unknown>(obj: T & RejectTypeEntity<T>): Effect.Effect<T, never, Service> =>
  Service.pipe(Effect.map(({ db }) => db.add<T>(obj))).pipe(Effect.withSpan('Database.add'));

/**
 * Persists a Type definition to the database.
 * @see {@link Database.addType}
 */
export const addType = <T extends Type.AnyEntity>(type: T): Effect.Effect<T, never, Service> =>
  Service.pipe(Effect.flatMap(({ db }) => Effect.promise(() => db.addType(type)))).pipe(
    Effect.withSpan('Database.addType'),
  );

/**
 * Removes an object from the database.
 * @see {@link Database.remove}
 */
export const remove = <T extends Entity.Unknown>(obj: T): Effect.Effect<void, never, Service> =>
  Service.pipe(Effect.map(({ db }) => db.remove(obj))).pipe(Effect.withSpan('Database.remove'));

/**
 * Appends entities to a feed.
 * @see {@link Database.appendToFeed}
 */
export const appendToFeed = (feed: Feed.Feed, entities: Entity.Unknown[]): Effect.Effect<void, never, Service> =>
  Service.pipe(Effect.flatMap(({ db }) => Effect.promise(() => db.appendToFeed(feed, entities)))).pipe(
    Effect.withSpan('Database.appendToFeed'),
  );

/**
 * Removes entities from a feed.
 * @see {@link Database.deleteFromFeed}
 */
export const deleteFromFeed = (feed: Feed.Feed, entities: Entity.Unknown[]): Effect.Effect<void, never, Service> =>
  Service.pipe(Effect.flatMap(({ db }) => Effect.promise(() => db.deleteFromFeed(feed, entities)))).pipe(
    Effect.withSpan('Database.deleteFromFeed'),
  );

/**
 * Flushes pending changes to disk.
 * @see {@link Database.flush}
 */
export const flush = (opts?: FlushOptions) =>
  Service.pipe(Effect.flatMap(({ db }) => Effect.promise(() => db.flush(opts)))).pipe(
    Effect.withSpan('Database.flush'),
  );

/**
 * Creates a `QueryResult` object that can be subscribed to.
 */
export const query: {
  <Q extends Query.Any>(query: Q): QueryResult.QueryResultEffect<Query.Type<Q>, never, Service>;
  <F extends Filter.Any>(filter: F): QueryResult.QueryResultEffect<Filter.Type<F>, never, Service>;
} = (queryOrFilter: Query.Any | Filter.Any) =>
  Service.pipe(
    Effect.map(({ db }) => db.query(queryOrFilter as any) as QueryResult.QueryResult<any>),
    Effect.withSpan('Database.query'),
    queryInternal.makeQueryResultEffect,
  );

/**
 * Sync state of the database in relation to EDGE.
 */
export interface SyncState {
  //
  // Automerge
  //

  /**
   * Total number of documents locally.
   */
  readonly localDocumentCount: number;
  /**
   * Total number of documents on the remote peer.
   */
  readonly remoteDocumentCount: number;
  /**
   * Total number of documents across this peer and the remote peer.
   */
  readonly totalDocumentCount: number;
  /**
   * Total number of documents that are not synced.
   * Includes documents that are present only locally, only on the remote peer, or whether the peers have different versions.
   */
  readonly unsyncedDocumentCount: number;

  //
  // Feeds.
  //

  /**
   * Blocks still to pull from remote. 0 when caught up.
   */
  readonly blocksToPull: string;
  /**
   * Unpositioned blocks still to push to remote. 0 when caught up.
   */
  readonly blocksToPush: string;
  /**
   * Total blocks stored locally for this namespace in the space.
   */
  readonly totalBlocks: string;
}

/**
 * Options for reading combined sync state.
 */
export interface GetSyncStateOptions {
  /**
   * Peer to report the automerge document backlog against. Defaults to the EDGE peer.
   * Provide explicitly in local/test topologies where there is no EDGE peer.
   */
  readonly peerId?: string;
}

/**
 * Get the current sync state.
 */
export const getSyncState = (options?: GetSyncStateOptions): Effect.Effect<SyncState, never, Service> =>
  Service.pipe(Effect.flatMap(({ db }) => Effect.promise(() => db.getSyncState(options))));

/**
 * Subscribe to sync state changes.
 */
export const subscribeToSyncState = (options?: GetSyncStateOptions): Stream.Stream<SyncState, never, Service> =>
  Stream.asyncScoped((emit) =>
    Effect.gen(function* () {
      const { db } = yield* Service;
      const cleanup = db.subscribeToSyncState((state) => emit.single(state), options);
      yield* Effect.addFinalizer(() => Effect.sync(cleanup));
    }),
  );
